// Enums and constants for table keys
export const ContentDetailTableKey = {
  Overview: 'overview',
  ProblemStatement: 'problemStatement',
  PainPoint: 'painPoint',
  GapToImprove: 'gapToImprove',
  StrategicObjective: 'strategicObjective',
  BusinessImpact: 'businessImpact',
  EndGoals: 'endGoals',
  Pillar: 'pillar',
  UseCases: 'useCases',
  Technology: 'technology',
  KeyChallenges: 'keyChallenges',
  TechnologyImage: 'technologyImage',
  Timeline: 'timeline',
  Permission: 'permission',
} as const;

export type ContentDetailTableKey = typeof ContentDetailTableKey[keyof typeof ContentDetailTableKey];

// Interfaces for each table's data
export interface OverviewData {
  dxNo: string;
  titleTh: string;
  titleEn: string;
  shortDescTh: string;
  shortDescEn: string;
}

export interface ProblemStatementData {
  statementTh: string;
  statementEn: string;
}

export interface PainPointItem {
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
}
export type PainPointData = PainPointItem[];

export interface GapToImproveItem {
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
}
export type GapToImproveData = GapToImproveItem[];

export interface StrategicObjectiveItem {
  objectiveTh: string;
  objectiveEn: string;
}
export type StrategicObjectiveData = StrategicObjectiveItem[];

export interface BusinessImpactItem {
  impactTh: string;
  impactEn: string;
}
export type BusinessImpactData = BusinessImpactItem[];

export interface EndGoalItem {
  goalTh: string;
  goalEn: string;
}
export type EndGoalsData = EndGoalItem[];

export interface PillarItem {
  pillarTh: string;
  pillarEn: string;
}
export type PillarData = PillarItem[];

export interface UseCaseDetails {
  currentStepTh: string;
  currentStepEn: string;
  nextMoveTh: string;
  nextMoveEn: string;
  potentialValueTh: string;
  potentialValueEn: string;
}

export interface UseCaseItem {
  nameTh: string;
  nameEn: string;
  details: UseCaseDetails[];
}
export type UseCasesData = UseCaseItem[];

export interface TechnologyData {
  titleTh: string;
  titleEn: string;
}

export interface KeyChallengeItem {
  challengeTh: string;
  challengeEn: string;
}
export type KeyChallengesData = KeyChallengeItem[];

export interface TechnologyImageData {
  images: string[]; // URLs or file names
}

export interface TimelineItem {
  phaseNameTh: string;
  phaseNameEn: string;
  durationTh: string;
  durationEn: string;
  status: 'inProgress' | 'published';
}
export type TimelineData = TimelineItem[];

export interface PermissionData {
  owner: string;
  member: string[]; // Changed to array to support multiple members
}
export interface ContentDetailData {
  [ContentDetailTableKey.Overview]: OverviewData;
  [ContentDetailTableKey.ProblemStatement]: ProblemStatementData;
  [ContentDetailTableKey.PainPoint]: PainPointData;
  [ContentDetailTableKey.GapToImprove]: GapToImproveData;
  [ContentDetailTableKey.StrategicObjective]: StrategicObjectiveData;
  [ContentDetailTableKey.BusinessImpact]: BusinessImpactData;
  [ContentDetailTableKey.EndGoals]: EndGoalsData;
  [ContentDetailTableKey.Pillar]: PillarData;
  [ContentDetailTableKey.UseCases]: UseCasesData;
  [ContentDetailTableKey.Technology]: TechnologyData;
  [ContentDetailTableKey.KeyChallenges]: KeyChallengesData;
  [ContentDetailTableKey.TechnologyImage]: TechnologyImageData;
  [ContentDetailTableKey.Timeline]: TimelineData;
  [ContentDetailTableKey.Permission]: PermissionData;
}

// sample of ContentDetailData
export const sampleContentDetailData: ContentDetailData = {
  [ContentDetailTableKey.Overview]: {
    dxNo: 'DX001',
    titleTh: 'การจำลองกระบวนการและการออกแบบ',
    titleEn: 'Process & Design Simulation',
    shortDescTh: 'การจำลองกระบวนการและการออกแบบเพื่อเพิ่มประสิทธิภาพ',
    shortDescEn: 'Process and design simulation to enhance efficiency',
  },
  [ContentDetailTableKey.ProblemStatement]: {
    statementTh: 'ปัญหาที่ต้องการแก้ไขในกระบวนการผลิต',
    statementEn: 'Issues to be addressed in the production process',
  },
  [ContentDetailTableKey.PainPoint]: [
    {
      titleTh: 'ความล่าช้าในการผลิต',
      titleEn: 'Production Delays',
      descTh: 'กระบวนการผลิตที่ซับซ้อนทำให้เกิดความล่าช้า',
      descEn: 'Complex production processes causing delays',
    },
    {
      titleTh: 'ต้นทุนสูง',
      titleEn: 'High Costs',
      descTh: 'ค่าใช้จ่ายในการดำเนินงานที่เพิ่มขึ้น',
      descEn: 'Increasing operational expenses',
    },
  ],
  [ContentDetailTableKey.GapToImprove]: [
    {
      titleTh: 'การวางแผนที่ไม่ดี',
      titleEn: 'Poor Planning',
      descTh: 'ขาดการวางแผนที่มีประสิทธิภาพในกระบวนการผลิต',
      descEn: 'Lack of effective planning in production processes',
    },
    {
      titleTh: 'การสื่อสารที่ไม่ชัดเจน',
      titleEn: 'Unclear Communication',
      descTh: 'ปัญหาในการสื่อสารระหว่างทีมงาน',
      descEn: 'Issues in communication among teams',
    },
  ],
  [ContentDetailTableKey.StrategicObjective]: [
    { objectiveTh: 'เพิ่มประสิทธิภาพการผลิต', objectiveEn: 'Enhance Production Efficiency' },
    { objectiveTh: 'ลดต้นทุนการดำเนินงาน', objectiveEn: 'Reduce Operational Costs' },
  ],
  [ContentDetailTableKey.BusinessImpact]: [
    { impactTh: 'เพิ่มรายได้', impactEn: 'Increase Revenue' },
    { impactTh: 'ปรับปรุงความพึงพอใจของลูกค้า', impactEn: 'Improve Customer Satisfaction' },
  ],
  [ContentDetailTableKey.EndGoals]: [
    { goalTh: 'ระบบการผลิตที่มีประสิทธิภาพ', goalEn: 'Efficient Production System' },
    { goalTh: 'ลดเวลาการผลิต', goalEn: 'Reduce Production Time' },
  ],
  [ContentDetailTableKey.Pillar]: [
    { pillarTh: 'เทคโนโลยีขั้นสูง', pillarEn: 'Advanced Technology' },
    { pillarTh: 'การฝึกอบรมพนักงาน', pillarEn: 'Employee Training' },
  ],
  [ContentDetailTableKey.UseCases]: [
    {
      nameTh: 'การจำลองกระบวนการผลิต',
      nameEn: 'Production Process Simulation',
      details: [
        {
          currentStepTh: 'วิเคราะห์กระบวนการปัจจุบัน',
          currentStepEn: 'Analyze Current Processes',
          nextMoveTh: 'พัฒนาระบบจำลอง',
          nextMoveEn: 'Develop Simulation System',
          potentialValueTh: 'ลดความล่าช้าและต้นทุน',
          potentialValueEn: 'Reduce Delays and Costs',
        },
        {
          currentStepTh: 'ทดสอบและปรับปรุง',
          currentStepEn: 'Test and Refine',
          nextMoveTh: 'นำระบบไปใช้จริง',
          nextMoveEn: 'Implement System',
          potentialValueTh: 'เพิ่มประสิทธิภาพการผลิต',
          potentialValueEn: 'Enhance Production Efficiency',
        },
        {
          currentStepTh: 'ฝึกอบรมพนักงาน',
          currentStepEn: 'Train Employees',
          nextMoveTh: 'ติดตามผลและปรับปรุงต่อเนื่อง',
          nextMoveEn: 'Monitor and Continuously Improve',
          potentialValueTh: 'รักษาคุณภาพการผลิต',
          potentialValueEn: 'Maintain Production Quality',
        }
      ],
    },
    {
      nameTh: 'การเพิ่มประสิทธิภาพการออกแบบ',
      nameEn: 'Design Optimization',
      details: [
        {
          currentStepTh: 'ทบทวนการออกแบบปัจจุบัน',
          currentStepEn: 'Review Current Designs',
          nextMoveTh: 'ใช้เครื่องมือการออกแบบขั้นสูง',
          nextMoveEn: 'Utilize Advanced Design Tools',
          potentialValueTh: 'ปรับปรุงคุณภาพและลดข้อผิดพลาด',
          potentialValueEn: 'Improve Quality and Reduce Errors',
        },
        {
          currentStepTh: 'ทดสอบการออกแบบใหม่',
          currentStepEn: 'Test New Designs',
          nextMoveTh: 'นำการออกแบบไปใช้จริง',
          nextMoveEn: 'Implement Designs',
          potentialValueTh: 'เพิ่มความพึงพอใจของลูกค้า',
          potentialValueEn: 'Enhance Customer Satisfaction',
        },
        {
          currentStepTh: 'รวบรวมข้อเสนอแนะ',
          currentStepEn: 'Gather Feedback',
          nextMoveTh: 'ปรับปรุงการออกแบบอย่างต่อเนื่อง',
          nextMoveEn: 'Continuously Improve Designs',
          potentialValueTh: 'รักษาความสามารถในการแข่งขัน',
          potentialValueEn: 'Maintain Competitiveness',
        }
      ],
    },
  ],
  [ContentDetailTableKey.Technology]: {
    titleTh: 'เทคโนโลยีการจำลองและการวิเคราะห์ขั้นสูง',
    titleEn: 'Advanced Simulation and Analytics Technology',
  },
  [ContentDetailTableKey.KeyChallenges]: [
    {
      challengeTh: 'การบูรณาการกับระบบเดิม',
      challengeEn: 'Integration with Legacy Systems',
    },
    {
      challengeTh: 'การฝึกอบรมพนักงาน',
      challengeEn: 'Employee Training',
    },
  ],
  [ContentDetailTableKey.TechnologyImage]: {
    images: ['tech_image1.png', 'tech_image2.png'],
  },
  [ContentDetailTableKey.Timeline]: [
    {
      phaseNameTh: 'การวางแผน',
      phaseNameEn: 'Planning',
      durationTh: '1 เดือน',
      durationEn: '1 Month',
      status: 'inProgress',
    },
    {
      phaseNameTh: 'การพัฒนา',
      phaseNameEn: 'Development',
      durationTh: '3 เดือน',
      durationEn: '3 Months',
      status: 'inProgress',
    },
    {
      phaseNameTh: 'การทดสอบ',
      phaseNameEn: 'Testing',
      durationTh: '2 เดือน',
      durationEn: '2 Months',
      status: 'published',
    },
    {
      phaseNameTh: 'การปรับใช้',
      phaseNameEn: 'Deployment',
      durationTh: '1 เดือน',
      durationEn: '1 Month',
      status: 'inProgress',
    },
  ],
  [ContentDetailTableKey.Permission]: {
    owner: 'admin@example.com',
    member: ['editor@example.com'],
  },
};

export const emptyContentDetailData: ContentDetailData = {
    [ContentDetailTableKey.Overview]: {
      dxNo: '',
      titleTh: '',
      titleEn: '',
      shortDescTh: '',
      shortDescEn: '',
    },
    [ContentDetailTableKey.ProblemStatement]: {
      statementTh: '',
      statementEn: '',
    },
    [ContentDetailTableKey.PainPoint]: [
      { titleTh: '', titleEn: '', descTh: '', descEn: '' },
    ],
    [ContentDetailTableKey.GapToImprove]: [
      { titleTh: '', titleEn: '', descTh: '', descEn: '' },
    ],
    [ContentDetailTableKey.StrategicObjective]: [
      { objectiveTh: '', objectiveEn: '' },
    ],
    [ContentDetailTableKey.BusinessImpact]: [
      { impactTh: '', impactEn: '' },
    ],
    [ContentDetailTableKey.EndGoals]: [
      { goalTh: '', goalEn: '' },
    ],
    [ContentDetailTableKey.Pillar]: [
      { pillarTh: '', pillarEn: '' },
    ],
    [ContentDetailTableKey.UseCases]: [
      {
        nameTh: '',
        nameEn: '',
        details: [
          {
            currentStepTh: '',
            currentStepEn: '',
            nextMoveTh: '',
            nextMoveEn: '',
            potentialValueTh: '',
            potentialValueEn: '',
          }
        ],
      },
    ],
    [ContentDetailTableKey.Technology]: {
      titleTh: '',
      titleEn: '',
    },
    [ContentDetailTableKey.KeyChallenges]: [
      { challengeTh: '', challengeEn: '' },
    ],
    [ContentDetailTableKey.TechnologyImage]: {
      images: [],
    },
    [ContentDetailTableKey.Timeline]: [
      {
        phaseNameTh: '',
        phaseNameEn: '',
        durationTh: '',
        durationEn: '',
        status: 'inProgress',
      },
    ],
    [ContentDetailTableKey.Permission]: {
      owner: '',
      member: [],
    },
  };